import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { siteGetSites, accountUpdateUser } from '../api';
import { 
  SmartField_Api_Contracts_Account_UserListItemDto,
  SmartField_Api_Contracts_Account_UserUpdateDto,
  SmartField_Authorization_Contracts_ClaimDto 
} from '../api/types.gen';
import { useIsAdmin, useAffiliatedSiteIds } from '../lib/auth';
import { SmartFieldClaimType } from '../lib/auth/types';

interface SitePermissions {
  siteId: number;
  siteName: string;
  userManager: SmartFieldClaimType;
  siteManager: SmartFieldClaimType;
  rawData: SmartFieldClaimType;
  fluxData: SmartFieldClaimType;
}

interface EditUserModalProps {
  user: SmartField_Api_Contracts_Account_UserListItemDto;
  isOpen: boolean;
  onClose: () => void;
}

export function EditUserModal({ user, isOpen, onClose }: EditUserModalProps) {
  const queryClient = useQueryClient();
  const isAdmin = useIsAdmin();
  const userSiteIds = useAffiliatedSiteIds();

  const [activeTab, setActiveTab] = useState<number>(0);
  const [sitePermissions, setSitePermissions] = useState<Map<number, SitePermissions>>(new Map());
  const [isUserAdmin, setIsUserAdmin] = useState(false);

  const { data: sites, isLoading: sitesLoading } = useQuery({
    queryKey: ['sites'],
    queryFn: () => siteGetSites(),
  });

  // Admins can see all sites, UserManagers only see their affiliated sites
  const availableSites = isAdmin 
    ? sites 
    : sites?.filter(site => site.id && userSiteIds.includes(site.id));

  // Parse existing user claims into permissions structure
  useEffect(() => {
    if (availableSites && availableSites.length > 0 && isOpen) {
      const permissionsMap = new Map<number, SitePermissions>();
      
      // Initialize all available sites with None permissions
      availableSites.forEach(site => {
        if (site.id) {
          permissionsMap.set(site.id, {
            siteId: site.id,
            siteName: site.name || '',
            userManager: SmartFieldClaimType.None,
            siteManager: SmartFieldClaimType.None,
            rawData: SmartFieldClaimType.None,
            fluxData: SmartFieldClaimType.None,
          });
        }
      });

      // Parse user's existing claims
      user.claims.forEach(claim => {
        const siteId = parseInt(claim.value, 10);
        if (!isNaN(siteId) && permissionsMap.has(siteId)) {
          const perms = permissionsMap.get(siteId)!;
          
          // Match claim type to permission field
          if (claim.type === SmartFieldClaimType.UserManager) {
            perms.userManager = claim.type as SmartFieldClaimType;
          } else if (claim.type === SmartFieldClaimType.SiteManager) {
            perms.siteManager = claim.type as SmartFieldClaimType;
          } else if ([SmartFieldClaimType.RawDataEditor, SmartFieldClaimType.RawDataExporter, SmartFieldClaimType.RawDataReader].includes(claim.type as SmartFieldClaimType)) {
            perms.rawData = claim.type as SmartFieldClaimType;
          } else if ([SmartFieldClaimType.FluxDataEditor, SmartFieldClaimType.FluxDataExporter, SmartFieldClaimType.FluxDataReader].includes(claim.type as SmartFieldClaimType)) {
            perms.fluxData = claim.type as SmartFieldClaimType;
          }
        }
      });

      setSitePermissions(permissionsMap);
      setActiveTab(availableSites[0]?.id || 0);
      
      // Check if user is admin
      const hasAdminRole = user.claims.some(c => 
        c.type === 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role' && 
        c.value === 'Admin'
      );
      setIsUserAdmin(hasAdminRole);
    }
  }, [availableSites, user, isOpen]);

  const updateMutation = useMutation({
    mutationFn: (updateDto: SmartField_Api_Contracts_Account_UserUpdateDto) =>
      accountUpdateUser({ 
        userId: user.id,
        requestBody: updateDto 
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
  });

  const buildClaims = (): SmartField_Authorization_Contracts_ClaimDto[] => {
    const claims: SmartField_Authorization_Contracts_ClaimDto[] = [];

    if (isUserAdmin) {
      claims.push({
        type: 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role',
        value: 'Admin',
      });
    }

    sitePermissions.forEach((perms) => {
      if (perms.userManager !== SmartFieldClaimType.None) {
        claims.push({
          type: perms.userManager,
          value: perms.siteId.toString(),
        });
      }
      if (perms.siteManager !== SmartFieldClaimType.None) {
        claims.push({
          type: perms.siteManager,
          value: perms.siteId.toString(),
        });
      }
      if (perms.rawData !== SmartFieldClaimType.None) {
        claims.push({
          type: perms.rawData,
          value: perms.siteId.toString(),
        });
      }
      if (perms.fluxData !== SmartFieldClaimType.None) {
        claims.push({
          type: perms.fluxData,
          value: perms.siteId.toString(),
        });
      }
    });

    return claims;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const claims = buildClaims();
    const updateRequest: SmartField_Api_Contracts_Account_UserUpdateDto = {
      userId: user.id,
      claims,
    };

    updateMutation.mutate(updateRequest);
  };

  const updateSitePermission = (
    siteId: number,
    field: keyof Omit<SitePermissions, 'siteId' | 'siteName'>,
    value: SmartFieldClaimType
  ) => {
    setSitePermissions(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(siteId);
      if (existing) {
        newMap.set(siteId, { ...existing, [field]: value });
      }
      return newMap;
    });
  };

  if (!isOpen) return null;

  const activeSitePermissions = sitePermissions.get(activeTab);

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
          aria-hidden="true"
        />

        {/* Modal panel */}
        <div 
          className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full"
          onClick={(e) => e.stopPropagation()}
        >
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Edit User Permissions - {user.email}
                </h3>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {updateMutation.error && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-700">{String(updateMutation.error)}</p>
                </div>
              )}

              {/* Admin checkbox (only for admins) */}
              {isAdmin && (
                <div className="mb-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={isUserAdmin}
                      onChange={(e) => setIsUserAdmin(e.target.checked)}
                      className="rounded border-gray-300 text-smartfield focus:ring-smartfield"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">
                      Register as Administrator
                    </span>
                  </label>
                  <p className="ml-6 mt-1 text-xs text-gray-500">
                    Administrators have full access to all features and sites
                  </p>
                </div>
              )}

              {/* Site Tabs */}
              {availableSites && availableSites.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Site Permissions
                  </label>
                  
                  <div className="border-b border-gray-200 mb-4">
                    <nav className="-mb-px flex space-x-4 overflow-x-auto">
                      {availableSites.map(site => (
                        <button
                          key={site.id}
                          type="button"
                          onClick={() => setActiveTab(site.id!)}
                          className={`whitespace-nowrap py-2 px-3 border-b-2 font-medium text-sm ${
                            activeTab === site.id
                              ? 'border-smartfield text-smartfield'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          {site.name}
                        </button>
                      ))}
                    </nav>
                  </div>

                  {activeSitePermissions && (
                    <div className="space-y-6">
                      {/* User Management */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          User Management
                        </label>
                        <select
                          value={activeSitePermissions.userManager}
                          onChange={(e) => updateSitePermission(activeTab, 'userManager', e.target.value as SmartFieldClaimType)}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-smartfield focus:ring-smartfield bg-white text-gray-900"
                        >
                          <option value={SmartFieldClaimType.None}>None</option>
                          <option value={SmartFieldClaimType.UserManager}>User Manager</option>
                        </select>
                      </div>

                      {/* Site Management */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Site Management
                        </label>
                        <select
                          value={activeSitePermissions.siteManager}
                          onChange={(e) => updateSitePermission(activeTab, 'siteManager', e.target.value as SmartFieldClaimType)}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-smartfield focus:ring-smartfield bg-white text-gray-900"
                        >
                          <option value={SmartFieldClaimType.None}>None</option>
                          <option value={SmartFieldClaimType.SiteManager}>Site Manager</option>
                        </select>
                      </div>

                      {/* Raw Data Access */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Raw Data Access
                        </label>
                        <select
                          value={activeSitePermissions.rawData}
                          onChange={(e) => updateSitePermission(activeTab, 'rawData', e.target.value as SmartFieldClaimType)}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-smartfield focus:ring-smartfield bg-white text-gray-900"
                        >
                          <option value={SmartFieldClaimType.None}>None</option>
                          <option value={SmartFieldClaimType.RawDataReader}>Reader</option>
                          <option value={SmartFieldClaimType.RawDataExporter}>Exporter</option>
                          <option value={SmartFieldClaimType.RawDataEditor}>Editor</option>
                        </select>
                      </div>

                      {/* Flux Data Access */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Flux Data Access
                        </label>
                        <select
                          value={activeSitePermissions.fluxData}
                          onChange={(e) => updateSitePermission(activeTab, 'fluxData', e.target.value as SmartFieldClaimType)}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-smartfield focus:ring-smartfield bg-white text-gray-900"
                        >
                          <option value={SmartFieldClaimType.None}>None</option>
                          <option value={SmartFieldClaimType.FluxDataReader}>Reader</option>
                          <option value={SmartFieldClaimType.FluxDataExporter}>Exporter</option>
                          <option value={SmartFieldClaimType.FluxDataEditor}>Editor</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {sitesLoading && (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-smartfield"></div>
                </div>
              )}
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
              <button
                type="submit"
                disabled={updateMutation.isPending}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-smartfield text-base font-medium text-white hover:bg-smartfield-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-smartfield sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
              >
                {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-smartfield sm:mt-0 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
}
